import { ApiFragment } from '@stylehaven/components';
import { ActionStatus } from './action-status.entity';
import { UserActionBuilder } from './user-action-builder.entity';
import { ActionDisabledCondition, UserAction } from './user-action.entity';

export class BulkUserAction {
    private actionStatus: ActionStatus;
    private readonly userActions: UserAction[];
    private readonly availableConditions: boolean[];
    private readonly disabledConditions: ActionDisabledCondition[];
    public records: ApiFragment[];

    constructor(
        records: readonly ApiFragment[],
        availableConditions: boolean[],
        disabledConditions: ActionDisabledCondition[],
        userActionBuilder: UserActionBuilder,
        excludeSingleCheck = false,
        ...args: any
    ) {
        this.userActions = records.map(record => userActionBuilder(record, ...args));
        this.availableConditions = availableConditions;
        this.disabledConditions = disabledConditions;
        this.records = records.map(record => record);

        this.initActionStatus(excludeSingleCheck);

        const generalDisabledCondition: ActionDisabledCondition = {
            condition: this.isDisabled(),
            tooltip: 'general.bulk-actions'
        };
        this.disabledConditions.push(generalDisabledCondition);
    }

    /**
     * Bulkaction Status
     *
     * Actionable → actionable for all
     * Unavailable → not available for all
     * Disabled → other (mixed cases)
     */
    private initActionStatus(excludeSingleCheck: boolean): void {
        const unavailableOnAll: boolean = this.userActions.every(userAction => !userAction.isAvailable());
        const bulkAvailable: boolean = this.availableConditions.every(condition => condition);
        const unavailable: boolean = unavailableOnAll || !bulkAvailable;

        if (unavailable) {
            this.actionStatus = ActionStatus.Unavailable;
            return;
        }

        const singleDisabled: boolean = this.userActions.some(
            userAction => userAction.isDisabled() || !userAction.isAvailable()
        );
        const bulkDisabled: boolean = this.disabledConditions.some(disabledCondition => disabledCondition.condition);

        const disabled = (singleDisabled && !excludeSingleCheck) || bulkDisabled;
        if (singleDisabled) {
            this.records = this.records.filter(
                (record, index) => !this.userActions[index].isDisabled() && this.userActions[index].isAvailable()
            );
        }

        this.actionStatus = disabled ? ActionStatus.NotActionable : ActionStatus.Actionable;
    }

    public getDisabledCondition(): ActionDisabledCondition | undefined {
        return this.disabledConditions.find(disabledCondition => disabledCondition.condition);
    }

    public isAvailable(): boolean {
        return this.actionStatus !== ActionStatus.Unavailable;
    }

    public isDisabled(): boolean {
        return this.actionStatus === ActionStatus.NotActionable;
    }

    public isEnabled(): boolean {
        return this.actionStatus === ActionStatus.Actionable;
    }
}
